
import { MHeader, MDialogV2, MError } from '@/pages/components/index'
import { getRequestLanguage } from '@/api/utils'
import { defineComponent, reactive, toRefs, computed, onBeforeMount, onMounted, nextTick, defineAsyncComponent } from 'vue'
import scssVariables from '@/utils/scssVariables'
import { Button, Toast, Image as VanImage, ImagePreview } from 'vant'
import { autoRem } from '@/utils/remUtils'
import portrait from '@/assets/img/default_portrait.png'
import { DynamicDataSucType, DynamicMediaItemType } from '@/api/types/dynamic'
import { useI18n } from 'vue-i18n'
import { useCallApp } from '@/hooks/useCallApp'
import UaParser from 'ua-parser-js'
import { isIpad } from '@/utils/is'
import { isInAppUA } from '@/utils/uaConfig'
import { getDynamicData } from '@/api/request/dynamic'
import { handleDownLoadWeelife3d } from '@/utils/file/download'
import { postSLSReportData } from '@/api/request/sls'
import { urlParam2Obj } from '@/utils/url'
import { useClipboardV2 } from '@/hooks/useClipboardV2'
import { processDynamicContent } from '../utils/dynamic'

interface IDynamicState {
  qrCodeUrl: string
  isShowMDialog: boolean
  dynamicData: DynamicDataSucType
  portrait: string
  userId: string
  contentId: string
  dynamicPicList: DynamicMediaItemType[]
  error: boolean
  dynamicAudioList: DynamicMediaItemType[]
  dynamicAudioCoverList: DynamicMediaItemType[]
}

enum DynamicButtonType {
  Play = 1,
  Follow = 2,
  OpenAppForMoreContent = 3,
}

export default defineComponent({
  name: 'Dynamic',
  components: {
    MHeader,
    MDialogV2,
    Button,
    MError,
    VanImage,
    DynamicClamp: defineAsyncComponent(() => import('../components/dynamic/dynamic-clamp.vue')),
    DynamicAudio: defineAsyncComponent(() => import('../components/dynamic/dynamic-audio.vue'))
  },
  setup() {
    const { t } = useI18n()
    const state = reactive<IDynamicState>({
      qrCodeUrl: `${window.location.href}?toPath=true`,
      isShowMDialog: false,
      portrait,
      userId: '',
      contentId: '',
      dynamicData: {} as DynamicDataSucType,
      dynamicPicList: [] as DynamicMediaItemType[],
      dynamicAudioList: [] as DynamicMediaItemType[],
      dynamicAudioCoverList: [] as DynamicMediaItemType[],
      error: false
    })
    const uaParser = new UaParser()
    const uaInfo = uaParser.getResult()
    autoRem(true)

    const language = computed(() => {
      return getRequestLanguage()
    })

    const setImageFit = () => {
      if (state.dynamicPicList.length >= 4) {
        return 'cover'
      }
      return 'contain'
    }

    const setImageStyle = (item: DynamicMediaItemType) => {
      const baseStyle = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        marginRight: '0px'
      }
      const { width, height } = item
      if (height > width * 2) {
        baseStyle.height = `${height * 0.3}px`
      }

      if (state.dynamicPicList.length >= 4) {
        baseStyle.width = 'calc(50% - 10px)'
        baseStyle.height = '120px'
      }

      return baseStyle
    }
    onBeforeMount(() => {
      const { userId, contentId } = urlParam2Obj()
      state.userId = userId || ''
      state.contentId = contentId || ''
      if (!state.userId || !state.contentId) {
        state.error = true
        return
      }
      getDynamicData(state.userId, state.contentId).then(res => {
        if (res && res.data) {
          state.dynamicData = res.data
          state.dynamicData.content = res.data.content ? processDynamicContent(res.data.content) : res.data.content
          state.dynamicAudioList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 2 && item.url) : []
          if (state.dynamicAudioList.length === 0) {
            state.dynamicPicList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 1 && item.url) : []
          } else if (state.dynamicAudioList.length > 0) {
            state.dynamicAudioCoverList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 1 && item.url) : []
          }
          return
        }
        state.error = true
      }).catch(err => {
        state.error = true
        console.error(err)
      })
    })

    onMounted(() => {
      postSLSReportData([{ eventName: 'post_show_web', customData: { author_uid: state.userId, post_id: state.contentId } }])
      nextTick(() => {
        handleCopyCode()
      })
    })

    const { clipboardCopy } = useClipboardV2()

    const handleCopyCode = () => {
      return new Promise((resolve) => {
        // 使用 copyText
        clipboardCopy(window.location.href, undefined, (event, status) => {
          if (status === 'success') {
            console.log('复制成功', window.location.href)
            resolve(true)
          } else {
            console.error('复制失败')
            resolve(false)
          }
        })
      })
    }

    const handleImagePreview = (list: any[], index: number) => {
      if (list.length === 0) {
        return
      }
      const imgList = list.map(item => item.url)
      ImagePreview({ images: [...imgList], startPosition: index })
    }

    const handleOpenApp = (type: DynamicButtonType) => {
      postSLSReportData([{ eventName: 'post_click_web', customData: { button_type: type } }])
      handleCopyCode()
      if (
        uaInfo.browser.name === 'WeChat' ||
        uaInfo.browser.name === 'Weibo'
      ) {
        state.isShowMDialog = true
        return
      }
      // 手机百度浏览器因官方封禁，绕不过去，需加白名单
      // 判断是否在app内打开（暂时不能走加入房间的逻辑）
      if (uaInfo.browser.name === 'baiduboxapp' || isInAppUA(uaInfo.ua)) {
        state.isShowMDialog = true
        return
      }

      /** 判断是否为ipad、android、ios */
      if (isIpad() || uaInfo.os.name === 'Android' || uaInfo.os.name === 'iOS') {
        handleCallApp()
        return
      }

      if (uaInfo.os.name !== 'Android' && uaInfo.os.name !== 'iOS') {
        state.isShowMDialog = true
      }
    }

    const handleDownLoad = () => {
      handleDownLoadWeelife3d()
    }

    // 唤起APP
    const handleCallApp = () => {
      postSLSReportData([{ eventName: 'post_open_app_by_web', customData: {} }])
      const options = {
        path: 'player.dynamic',
        param: {
          ...urlParam2Obj()
        }
      }
      useCallApp(options, (status) => {
        if (status === 'pending') {
          Toast.loading({
            message: `${t('public.wait')}`,
            forbidClick: true,
            duration: 3000
          })
        }
        if (status === 'failure') {
          state.isShowMDialog = true
        }

        if (status !== 'pending') {
        }
      })
    }

    const dynamicAudioPlay = () => {
      postSLSReportData([{ eventName: 'post_click_web', customData: { button_type: 4 } }])
    }

    const dynamicAudioPause = (duration: number, currentTime: number) => {
      const customData = {
        author_uid: state.userId,
        post_id: state.contentId,
        duration_time: Math.floor(currentTime) | 0, // 播放时长
        voice_time: Math.floor(duration) | 0 // 总时长
      }
      postSLSReportData([{ eventName: 'post_click_web_voice_pause', customData: { ...customData } }])
    }

    const dynamicAudioEnded = (duration: number, currentTime: number) => {
      const customData = {
        author_uid: state.userId,
        post_id: state.contentId,
        pause_type: currentTime >= duration ? 1 : 2, // int:pause_type(音频播放停止类型)1=自动播放完成；2=被动终止；
        duration_time: Math.floor(currentTime) | 0, // 播放时长
        voice_time: Math.floor(duration) | 0 // 总时长
      }
      postSLSReportData([{ eventName: 'post_voice_done_web', customData: { ...customData } }])
    }

    return {
      scssVariables,
      ...toRefs(state),
      language,
      setImageFit,
      setImageStyle,
      handleImagePreview,
      handleOpenApp,
      handleDownLoad,
      dynamicAudioPlay,
      dynamicAudioPause,
      dynamicAudioEnded
    }
  }
})
